import { loadStripe } from '@stripe/stripe-js'
import { useEffect, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { Button, ButtonColor, Loader, NotificationType } from '../../components'
import env from '../../config/env'
import axios from 'axios'
import {
  AgeGroups,
  Brand,
  Campaign,
  CampaignAddon,
  CountryCode,
  CreatorQuality,
  ModelType,
  VoiceType,
  Platform,
  Product,
  User,
  VideoDuration,
  VideoType,
  AspectRatio,
  CreatorPetsOwner,
  CampaignVisibility,
  BrandServiceLevel,
} from '../../gql/types'
import {
  useCreateCampaignMutation,
  useUpdateCampaignMutation,
} from '../../hooks'
import { useCreateCampaignCheckout } from '../../hooks/payments'
import {
  ethnicityLocalizedStringInverse,
  genderLocalizedStringInverse,
} from '../../utils/GqlStrings'
import {
  PricingData,
  formatPriceInCents,
  getAddonsPricePerUnit,
  getCampaignPricing,
  getPriceByQuality,
  getVideoPricing,
} from '../../utils/PricingUtil'
import { UrlBuilder } from '../../utils/UrlBuilder'
import { cleanUrl } from '../../utils/helpers'
import {
  isEmptyString,
  isObjectEmpty,
  useNotification,
} from '../../utils/hooks'

import {
  SetAddons,
  SetCreatorDetails,
  SetInspirationVideosLinks,
  SetModelType,
  SetVoiceType,
  SetProductDetails,
  SetQuantity,
  SetRequirements,
  SetTitle,
  SetVariations,
  SetVideoDuration,
  SetVideoType,
  SetVideoAspectRatio,
  SetCampaignVisibility,
  SetVideoPlacements,
  SetCustomCreatorPrice,
} from './components'
import { BrandRoutes } from '../BrandRoutes'
import { useMutation } from '@apollo/client'
import { GET_REQUIREMENTS_UPLOAD_URL } from '../../gql'
// Make sure to call `loadStripe` outside of a component’s render to avoid
// recreating the `Stripe` object on every render.
const stripePromise = loadStripe(env.STRIPE_KEY)

type CreateNewCampaignFormData = {
  title: string
  requestedProduct: Product
  videoType: VideoType
  modelType: ModelType
  voiceType: VoiceType
  videoDuration: VideoDuration
  platform: Platform
  modelGender: string
  ethnicity: string
  pets: string[]
  creatorPreferences: string
  quality: CreatorQuality
  ageGroups: (AgeGroups | 'Any')[]
  requirements: string
  countryCode: CountryCode
  hooks: string[]
  inspirationVideosLinks: string[]
  videoPlacements: string
  videoPlacementsOther: string
  defaultAspectRatio?: AspectRatio | null
  addons: CampaignAddon[] | []
  addonsSubtitlesMusic?: CampaignAddon | null
  addonsAspectRatios?: CampaignAddon[] | []
  campaignVisibility: CampaignVisibility
  customCreatorEarningsCents: number | null
}

interface IProps {
  user: User
  brand: Brand
  pricing: PricingData
}

const NewCampaign = ({ user, brand, pricing }: IProps) => {
  const navigate = useNavigate()
  const { addNotification } = useNotification()
  const isEnterpriseOrWhiteGlove =
    brand?.serviceLevel === BrandServiceLevel.enterprise ||
    brand?.serviceLevel === BrandServiceLevel.whiteGlove

  const [initiateCheckoutLoading, setInitiateCheckoutLoading] = useState(false)
  const [quantity, setQuantity] = useState(3)
  const [draftCampaign, setDraftCampaign] = useState(null)
  const [errorsNewCampaign, setErrorsNewCampaign] = useState(null)
  const [selectedPdfFile, setSelectedPdfFile] = useState<File | null>(null)
  const [uploadingPdf, setUploadingPdf] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [savingDraft, setSavingDraft] = useState(false)

  const { createCampaign, loading: loadingCreateCampaign } =
    useCreateCampaignMutation()
  const { updateCampaign, loading: loadingUpdateCampaign } =
    useUpdateCampaignMutation()
  const [getRequirementsUploadUrl] = useMutation(GET_REQUIREMENTS_UPLOAD_URL)

  const { createCampaignCheckout } = useCreateCampaignCheckout()

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    watch,
    trigger,
    setError,
    setValue,
  } = useForm<CreateNewCampaignFormData>({
    mode: 'onSubmit', // Default validation mode - less aggressive
    values: {
      title: null,
      requestedProduct: null,
      videoType: null,
      modelType: null,
      voiceType: null,
      videoDuration: VideoDuration.half,
      platform: null,
      modelGender: 'Any',
      ethnicity: 'Any',
      pets: [],
      creatorPreferences: null,
      quality: CreatorQuality.regular,
      ageGroups: ['Any'],
      requirements: null,
      countryCode: brand?.countryCode ?? null,
      hooks: [],
      inspirationVideosLinks: [],
      videoPlacements: null,
      videoPlacementsOther: null,
      addons: [],
      defaultAspectRatio: AspectRatio.vertical,
      addonsSubtitlesMusic: null,
      addonsAspectRatios: [],
      campaignVisibility: CampaignVisibility.public,
      customCreatorEarningsCents: null,
    },
  })

  // @ts-ignore
  const customCreatorEarningsCents = useWatch({
    control,
    name: 'customCreatorEarningsCents',
  }) as number | null

  // @ts-ignore
  const selectedAddons = [
    // @ts-ignore
    ...(watch('addonsSubtitlesMusic') ? [watch('addonsSubtitlesMusic')] : []),
    ...watch('addonsAspectRatios'),
  ] as CampaignAddon[] | []
  // @ts-ignore
  const selectedVideoDuration = watch('videoDuration') as VideoDuration
  const selectedQuality = watch('quality') as CreatorQuality

  const getExtraPriceCentsPremium = () => {
    return getPriceByQuality(
      CreatorQuality.premium,
      getVideoPricing({
        currency: brand?.currency,
        videoDuration: selectedVideoDuration,
        pricing,
      }).price,
    ).extraPriceCents
  }

  // TODO: Refactor — remove this entire useEffect and replace with RHF built-in revalidation.
  // This useEffect manually reimplements what react-hook-form does automatically with its
  // default `reValidateMode: 'onChange'` setting. Each field is manually triggered and errors
  // are cleared one by one, which is error-prone (every new field must be added in two places:
  // the trigger/setError block and the dependency array).
  //
  // Recommended fix:
  // 1. Keep `mode: 'onSubmit'` (validates on submit — least aggressive, good UX).
  // 2. RHF's default `reValidateMode: 'onChange'` already clears field errors automatically
  //    once the user corrects them — no manual trigger/setError needed.
  // 3. Delete this useEffect entirely. The form-level error banner (`setErrorsNewCampaign`)
  //    can be set in the `onError` callback of `handleSubmit(onSubmit, onError)` instead.
  // 4. Apply the same refactor to CampaignEdit/Edit.tsx (identical pattern).
  //
  // Reference: https://react-hook-form.com/docs/useform (mode / reValidateMode)
  useEffect(() => {
    if (isObjectEmpty(errors)) {
      setErrorsNewCampaign(null)
      return
    }

    if (trigger('title')) {
      setError('title', null)
    }

    if (trigger('requestedProduct')) {
      setError('requestedProduct', null)
    }

    if (trigger('videoType')) {
      setError('videoType', null)
    }

    if (trigger('modelType')) {
      setError('modelType', null)
    }

    if (trigger('voiceType')) {
      setError('voiceType', null)
    }

    if (trigger('hooks')) {
      setError('hooks', null)
    }

    if (trigger('inspirationVideosLinks')) {
      setError('inspirationVideosLinks', null)
    }

    if (trigger('videoPlacements')) {
      setError('videoPlacements', null)
    }

    if (trigger('customCreatorEarningsCents')) {
      setError('customCreatorEarningsCents', null)
    }

    if (!isObjectEmpty(errors)) {
      setErrorsNewCampaign(
        'Please check to ensure all required fields are filled out.',
      )
      return
    }

    if (isObjectEmpty(errors)) {
      setErrorsNewCampaign(null)
      return
    }
  }, [
    // @ts-ignore
    watch('title'),
    watch('requestedProduct'),
    watch('videoType'),
    watch('modelType'),
    watch('voiceType'),
    JSON.stringify(watch('hooks')),
    JSON.stringify(watch('inspirationVideosLinks')),
    watch('videoPlacements'),
    customCreatorEarningsCents,
    errors,
  ])

  const isEnterprise = brand?.serviceLevel === BrandServiceLevel.enterprise
  const enterpriseTotalCostCents =
    isEnterprise && customCreatorEarningsCents
      ? customCreatorEarningsCents * quantity
      : 0
  const hasInsufficientCredits =
    isEnterprise &&
    enterpriseTotalCostCents > 0 &&
    enterpriseTotalCostCents > brand.creditCents

  const totalPriceCents = getCampaignPricing({
    currency: brand?.currency,
    duration: selectedVideoDuration,
    quantity,
    quality: selectedQuality,
    // @ts-ignore
    hooksCount: watch('hooks').length,
    addons: selectedAddons,
    pricing,
  })

  // Create a callback function to get the selected file from SetRequirements
  const handlePdfFileChange = (file: File | null) => {
    setSelectedPdfFile(file)
  }

  const initiateCheckout = async () => {
    if (isEnterpriseOrWhiteGlove) {
      const isValid = await trigger('customCreatorEarningsCents')
      if (!isValid) {
        setErrorsNewCampaign(
          'Please check to ensure all required fields are filled out.',
        )
        return
      }
    }

    setInitiateCheckoutLoading(true)

    let campaign: Campaign = null
    try {
      campaign = await saveDraft({ isNavigateToCampaigns: false })
    } catch (error) {
      addNotification(
        `Something went wrong. Please try again or contact support at hello@useclip.com`,
        NotificationType.error,
      )
    }

    if (!campaign?.id) {
      setInitiateCheckoutLoading(false)
      return
    }

    try {
      const { data } = await createCampaignCheckout({
        variables: {
          brandId: brand.id,
          input: {
            campaignId: campaign.id,
            quantity,
            successRedirectUrlPath: UrlBuilder.buildCampaignsUrl(brand.id),
            cancelRedirectUrlPath: UrlBuilder.buildCampaignEditUrl(
              brand.id,
              campaign.id,
            ),
          },
        },
      })

      // if not present, campaign created with credits
      if (!data.createCampaignCheckout.stripeSessionId) {
        navigate(UrlBuilder.buildCampaignsUrl(brand.id))
        addNotification('Campaign launched.', NotificationType.success)
      }

      // if stripe session id present - redirect to stripe
      if (data.createCampaignCheckout.stripeSessionId) {
        const stripe = await stripePromise

        // When the customer clicks on the button, redirect them to Checkout.
        const result = await stripe.redirectToCheckout({
          sessionId: data.createCampaignCheckout.stripeSessionId,
        })

        if (result.error) {
          // If `redirectToCheckout` fails due to a browser or network
          // error, display the localized error message to your customer
          // using `result.error.message`.
          addNotification(
            `Something went wrong. Please try again or contact support at hello@useclip.com`,
            NotificationType.error,
          )
        }
      }
    } catch (error) {
      addNotification(
        `Something went wrong. Please try again or contact support at hello@useclip.com`,
        NotificationType.error,
      )
    }

    setInitiateCheckoutLoading(false)
  }

  const saveDraft = async (
    {
      isNavigateToCampaigns,
    }: {
      isNavigateToCampaigns?: boolean
    } = {
      isNavigateToCampaigns: false,
    },
  ) => {
    setSavingDraft(true)
    // @ts-ignore
    const isFormValidated = await trigger([
      'title',
      'requestedProduct',
      'videoType',
      'modelType',
      'voiceType',
      'inspirationVideosLinks',
      'hooks',
      'videoPlacements',
      'videoPlacementsOther',
      ...(isEnterpriseOrWhiteGlove ? ['customCreatorEarningsCents'] : []),
    ])

    if (!isFormValidated) {
      setErrorsNewCampaign(
        'Please check to ensure all required fields are filled out.',
      )
      setSavingDraft(false)
      return false
    }

    setErrorsNewCampaign(null)

    let ageGroups = null
    // @ts-ignore
    if (!watch('ageGroups').find((el) => el === 'Any')) {
      ageGroups = watch('ageGroups')
    }

    try {
      let updatedCampaign

      // Upload PDF if selected, but only after we have a campaign ID
      let fileData = null

      if (!draftCampaign) {
        const { data } = await createCampaign({
          variables: {
            brandId: brand.id,
            input: {
              // @ts-ignore
              title: watch('title'),
              // @ts-ignore
              productId: watch('requestedProduct')?.id,
              videoType: watch('videoType'),
              modelType: watch('modelType'),
              voiceType: watch('voiceType'),
              videoDuration: selectedVideoDuration,
              modelGender: genderLocalizedStringInverse(watch('modelGender')),
              ethnicity: ethnicityLocalizedStringInverse(watch('ethnicity')),
              creatorPreferences: watch('creatorPreferences'),
              quality: selectedQuality,
              ageGroups: ageGroups,
              // @ts-ignore
              pets: Array.isArray(watch('pets'))
                ? watch('pets').filter((pet) =>
                    Object.values(CreatorPetsOwner).includes(
                      // @ts-ignore
                      pet as CreatorPetsOwner,
                    ),
                  )
                : null,
              requirements: watch('requirements'),
              countryCode: watch('countryCode'),
              hooks: watch('hooks').filter((hook) => !isEmptyString(hook)),
              // @ts-ignore
              inspirationVideosLinks: watch('inspirationVideosLinks')
                .filter(
                  (inspirationVideosLink) =>
                    !isEmptyString(inspirationVideosLink),
                )
                .map((inspirationVideosLink) =>
                  cleanUrl(inspirationVideosLink),
                ),
              videoPlacements:
                watch('videoPlacements') === 'other'
                  ? watch('videoPlacementsOther')
                  : watch('videoPlacements'),
              addons: selectedAddons,
              aspectRatio: watch('defaultAspectRatio'),
              visibility: watch('campaignVisibility'),
              customCreatorEarningsCents: isEnterpriseOrWhiteGlove
                ? customCreatorEarningsCents
                : null,
            },
          },
        })

        updatedCampaign = data.createCampaign
        setDraftCampaign(updatedCampaign)

        // Now that we have a campaign ID, we can upload the PDF if it exists
        if (selectedPdfFile && updatedCampaign?.id) {
          fileData = await uploadPdfFile(selectedPdfFile, updatedCampaign.id)

          // Update the campaign again to save the PDF information
          if (fileData) {
            const { data: updateData } = await updateCampaign({
              variables: {
                id: updatedCampaign.id,
                input: {
                  requirementsName: fileData.fileName,
                  requirementsMime: fileData.mime,
                },
              },
            })

            updatedCampaign = updateData.updateCampaign
            setDraftCampaign(updatedCampaign)
          }
        }
      }

      if (draftCampaign) {
        // Try to upload PDF first if selected
        if (selectedPdfFile) {
          fileData = await uploadPdfFile(selectedPdfFile, draftCampaign.id)
        }

        const { data } = await updateCampaign({
          variables: {
            id: draftCampaign.id,
            input: {
              // @ts-ignore
              title: watch('title'),
              productId: watch('requestedProduct').id,
              videoType: watch('videoType'),
              modelType: watch('modelType'),
              voiceType: watch('voiceType'),
              videoDuration: selectedVideoDuration,
              modelGender: genderLocalizedStringInverse(watch('modelGender')),
              ethnicity: ethnicityLocalizedStringInverse(watch('ethnicity')),
              creatorPreferences: watch('creatorPreferences'),
              quality: selectedQuality,
              ageGroups: ageGroups,
              // @ts-ignore
              pets: Array.isArray(watch('pets'))
                ? watch('pets').filter((pet) =>
                    // @ts-ignore
                    Object.values(CreatorPetsOwner).includes(
                      pet as CreatorPetsOwner,
                    ),
                  )
                : null,
              requirements: watch('requirements'),
              requirementsName: fileData
                ? fileData.fileName
                : draftCampaign.requirementsName,
              requirementsMime: fileData
                ? fileData.mime
                : draftCampaign.requirementsMime,
              countryCode: watch('countryCode'),
              hooks: watch('hooks').filter((hook) => !isEmptyString(hook)),
              inspirationVideosLinks: watch('inspirationVideosLinks')
                .filter(
                  (inspirationVideosLink) =>
                    !isEmptyString(inspirationVideosLink),
                )
                .map((inspirationVideosLink) =>
                  cleanUrl(inspirationVideosLink),
                ),
              videoPlacements:
                watch('videoPlacements') === 'other'
                  ? watch('videoPlacementsOther')
                  : watch('videoPlacements'),
              addons: selectedAddons,
              aspectRatio: watch('defaultAspectRatio'),
              visibility: watch('campaignVisibility'),
              customCreatorEarningsCents: isEnterpriseOrWhiteGlove
                ? customCreatorEarningsCents
                : null,
            },
          },
        })

        updatedCampaign = data.updateCampaign
        setDraftCampaign(updatedCampaign)
      }

      // Reset PDF file after successful save
      setSelectedPdfFile(null)

      addNotification(`Campaign saved as draft.`, NotificationType.success)

      // Redirect to the campaigns page
      if (isNavigateToCampaigns && updatedCampaign?.id) {
        navigate(UrlBuilder.buildCampaignsUrl(brand.id))
      }

      return updatedCampaign
    } catch (error) {
      console.log('@error Edit saveDraft', error.message)
      addNotification(error.message, NotificationType.error)
    } finally {
      setSavingDraft(false)
    }
  }

  // Helper function to upload PDF file
  const uploadPdfFile = async (file: File, campaignId: string) => {
    if (!file || !campaignId) return null

    setUploadingPdf(true)
    setUploadProgress(0)

    try {
      // Get upload URL from API
      const { data } = await getRequirementsUploadUrl({
        variables: {
          campaignId: campaignId,
          mime: file.type,
        },
      })

      const { uploadUrl, fileName } = data.getRequirementsUploadUrl

      // Upload to S3 with progress tracking
      await axios.put(uploadUrl, file, {
        headers: {
          'Content-Type': file.type,
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total,
          )
          setUploadProgress(percentCompleted)
        },
      })

      return { fileName, mime: file.type }
    } catch (error) {
      console.error('Error uploading PDF:', error)
      addNotification(
        'Failed to upload PDF. Please try again.',
        NotificationType.error,
      )
      return null
    } finally {
      setUploadingPdf(false)
    }
  }

  if (!brand) {
    return <Loader />
  }

  return (
    <div className='pb-8'>
      <div className='sm:flex-auto py-4'>
        <div className='flex flex-row'>
          <h1 className='font-bold text-3xl font-outfit'>Create Campaign</h1>
        </div>
      </div>
      <div className='hidden sm:block' aria-hidden='true'>
        <div className='pb-6 border-t border-gray-200' />
      </div>
      <div className='-mx-4 sm:-mx-6 lg:-mx-8'>
        <div className='flex flex-1 flex-col'>
          <div className='sm:pt-6'>
            <form>
              <div className='mx-auto px-4 sm:px-6 md:px-8'>
                <SetTitle errors={errors} register={register} />
                <SetCampaignVisibility control={control} errors={errors} />
                <SetProductDetails
                  brand={brand}
                  control={control}
                  errors={errors}
                />
                <SetVideoType control={control} errors={errors} />
                <SetModelType control={control} errors={errors} />
                <SetVoiceType control={control} errors={errors} />
                {/* <SetPlatform control={control} errors={errors} /> */}
                <SetVideoDuration
                  control={control}
                  errors={errors}
                  currency={brand.currency}
                  quality={selectedQuality}
                  pricing={pricing}
                  hidePricing={isEnterpriseOrWhiteGlove}
                />
                <SetRequirements
                  errors={errors}
                  register={register}
                  campaign={draftCampaign}
                  onFileChange={handlePdfFileChange}
                  isUploading={uploadingPdf}
                  uploadProgress={uploadProgress}
                />
                <SetAddons
                  control={control}
                  errors={errors}
                  register={register}
                  watch={watch}
                  currency={brand.currency}
                  pricing={pricing}
                  hidePricing={isEnterpriseOrWhiteGlove}
                />
                <SetVideoAspectRatio
                  errors={errors}
                  register={register}
                  control={control}
                  watch={watch}
                  currency={brand.currency}
                  pricing={pricing}
                  setValue={setValue}
                  hidePricing={isEnterpriseOrWhiteGlove}
                />
                <SetVideoPlacements
                  errors={errors}
                  control={control}
                  watch={watch}
                  register={register}
                  trigger={trigger}
                />
                <SetVariations
                  errors={errors}
                  register={register}
                  watch={watch}
                  control={control}
                  videoPricing={getVideoPricing({
                    currency: brand?.currency,
                    // @ts-ignore
                    videoDuration: selectedVideoDuration,
                    pricing,
                  })}
                  hidePricing={isEnterpriseOrWhiteGlove}
                />
                <SetInspirationVideosLinks
                  errors={errors}
                  register={register}
                  watch={watch}
                  control={control}
                />
                <SetCreatorDetails
                  control={control}
                  errors={errors}
                  register={register}
                  watch={watch}
                  setValue={setValue}
                  priceExtraFormatted={formatPriceInCents({
                    priceInCents: getExtraPriceCentsPremium(),
                    currency: brand?.currency,
                  })}
                  hidePricing={isEnterpriseOrWhiteGlove}
                />
                {!isEnterpriseOrWhiteGlove && (
                  <SetQuantity
                    brand={brand}
                    quantity={quantity}
                    setQuantity={setQuantity}
                    totalPriceCents={totalPriceCents}
                    pricePerVideoCents={
                      getPriceByQuality(
                        selectedQuality,
                        getVideoPricing({
                          currency: brand?.currency,
                          videoDuration: selectedVideoDuration,
                          pricing,
                        })?.price,
                      )?.priceCents
                    }
                    pricePerVariationCents={
                      getVideoPricing({
                        currency: brand?.currency,
                        videoDuration: selectedVideoDuration,
                        pricing,
                      })?.hook?.priceCents
                    }
                    pricePerAddons={getAddonsPricePerUnit({
                      currency: brand?.currency,
                      addons: selectedAddons,
                      duration: selectedVideoDuration,
                      pricing,
                    })}
                    // @ts-ignore
                    hooksCount={watch('hooks')?.length ?? 0}
                    addonsCount={selectedAddons.length}
                  />
                )}
                {isEnterpriseOrWhiteGlove && (
                  <SetCustomCreatorPrice
                    brand={brand}
                    quantity={quantity}
                    setQuantity={setQuantity}
                    control={control}
                    errors={errors}
                  />
                )}
              </div>
            </form>
          </div>
          <div className='text-right px-4 sm:px-8'>
            {hasInsufficientCredits && (
              <div className='mb-4 rounded-md bg-yellow-50 border border-yellow-200 p-4 text-left'>
                <div className='flex'>
                  <div className='flex-shrink-0'>
                    <svg
                      className='h-5 w-5 text-yellow-400'
                      viewBox='0 0 20 20'
                      fill='currentColor'
                    >
                      <path
                        fillRule='evenodd'
                        d='M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 6a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 6zm0 9a1 1 0 100-2 1 1 0 000 2z'
                        clipRule='evenodd'
                      />
                    </svg>
                  </div>
                  <div className='ml-3'>
                    <h3 className='text-sm font-medium text-yellow-800'>
                      Insufficient credits
                    </h3>
                    <p className='mt-1 text-sm text-yellow-700'>
                      This campaign costs{' '}
                      {formatPriceInCents({
                        priceInCents: enterpriseTotalCostCents,
                        currency: brand?.currency,
                      })}{' '}
                      but you only have{' '}
                      {formatPriceInCents({
                        priceInCents: brand.creditCents,
                        currency: brand?.currency,
                      })}{' '}
                      in credits. Please contact your account manager to top up
                      your credits.
                    </p>
                  </div>
                </div>
              </div>
            )}
            <Button
              title='Save Draft'
              type='button'
              colour={ButtonColor.lightGray}
              className='mr-3'
              loading={
                loadingCreateCampaign ||
                loadingUpdateCampaign ||
                uploadingPdf ||
                savingDraft
              }
              disabled={
                loadingCreateCampaign ||
                loadingUpdateCampaign ||
                uploadingPdf ||
                savingDraft
              }
              loadingPercentage={
                uploadingPdf && savingDraft ? uploadProgress : undefined
              }
              onClick={() => saveDraft({ isNavigateToCampaigns: true })}
            />
            <Button
              title={
                isEnterpriseOrWhiteGlove ? 'Launch Campaign' : 'Complete Order'
              }
              type='button'
              loading={
                initiateCheckoutLoading ||
                (loadingCreateCampaign && !savingDraft) ||
                (loadingUpdateCampaign && !savingDraft) ||
                (uploadingPdf && !savingDraft)
              }
              disabled={
                hasInsufficientCredits ||
                initiateCheckoutLoading ||
                loadingCreateCampaign ||
                loadingUpdateCampaign ||
                uploadingPdf ||
                savingDraft
              }
              loadingPercentage={
                uploadingPdf && !savingDraft ? uploadProgress : undefined
              }
              onClick={initiateCheckout}
            />
            {errorsNewCampaign && (
              <p className='mt-2 text-sm text-red-600'>{errorsNewCampaign}</p>
            )}
          </div>
          <p className='mt-2 text-right text-sm text-gray-600 px-4 sm:px-8'>
            You'll be able to make edits to your campaign for 24 hours after you
            complete your order.
          </p>
        </div>
      </div>
    </div>
  )
}

export { NewCampaign }
